import { useSEO } from "@/lib/seo";
import { Link } from "react-router-dom";

const H = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-heading font-bold text-white text-lg mt-10 mb-3">{children}</h2>
);

export default function Terms() {
  useSEO("Terms of Service", "The terms that govern your use of NewsLayer.com.");
  return (
    <div data-testid="terms-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">Legal</p>
      <h1 className="font-heading font-black text-white text-3xl sm:text-5xl">Terms of Service</h1>
      <p className="font-mono text-xs text-gray-500 mt-3">Last updated: June 2026</p>

      <div className="text-gray-400 text-[15px] leading-relaxed mt-8 space-y-4">
        <p>
          By using NewsLayer.com ("NewsLayer", "we", "us") you agree to these terms. If you don't agree,
          please don't use the site.
        </p>

        <H>1. Not financial advice</H>
        <p className="text-gray-300">
          NewsLayer publishes news, analysis, market data, sentiment indicators and AI-assisted summaries about
          crypto assets and policy. <span className="text-white font-semibold">Nothing on NewsLayer is financial,
          investment, legal or tax advice.</span> Crypto assets are volatile and you can lose everything you put in.
          Always do your own research and consult a qualified professional before making financial decisions.
          Market data, the Layer Index and prediction polls are informational only and may be delayed or inaccurate.
        </p>

        <H>2. Your account</H>
        <p>
          You're responsible for your account credentials and activity. You must provide accurate information and
          be at least 16 years old. We may suspend accounts that abuse the platform, attempt to scrape at scale,
          interfere with service operation, or violate these terms.
        </p>

        <H>3. Paid products</H>
        <ul className="list-disc pl-5 space-y-2">
          <li><span className="text-gray-200 font-medium">Premium subscriptions</span> — billed via Stripe on the plan you choose; cancel anytime and access continues until the end of the paid period.</li>
          <li><span className="text-gray-200 font-medium">Advertising</span> — self-serve campaigns are governed by the Advertising Terms shown at booking. All campaigns are subject to editorial review; finally rejected campaigns are automatically refunded in full. Impressions, clicks or business results are not guaranteed.</li>
          <li><span className="text-gray-200 font-medium">PR publication</span> — paid announcements are reviewed before publishing and always labeled as sponsored/press-release content. Submissions that violate our content standards are rejected and refunded.</li>
          <li><span className="text-gray-200 font-medium">Author program</span> — the annual fee covers review and platform access; publication of individual submissions remains at our editorial discretion.</li>
        </ul>

        <H>4. Content & intellectual property</H>
        <p>
          NewsLayer's original content, design, wordmark and software are our property. Headlines and excerpts we
          aggregate remain the property of their sources and are always attributed and linked. You may share links
          and brief quotes with attribution; you may not republish our content wholesale or use it to train
          competing products without permission. Content you submit (author stories, PR, comments, poll votes)
          grants us a non-exclusive licence to publish and distribute it on NewsLayer and its channels.
        </p>

        <H>5. Acceptable use</H>
        <p>
          No scraping at scale, no attempting to bypass paywalls or access controls, no uploading malicious or
          unlawful content, no impersonating NewsLayer editorial, and no use of the platform to promote scams or
          misleading financial claims.
        </p>

        <H>6. Disclaimers & liability</H>
        <p>
          The service is provided "as is" without warranties of any kind. Some content is generated or translated
          with AI assistance and may contain errors — verify before relying on it. To the maximum extent permitted
          by law, NewsLayer's total liability for any claim is limited to the amount you paid us in the twelve
          months before the claim, and we are not liable for indirect or consequential losses, including trading
          losses.
        </p>

        <H>7. Changes & termination</H>
        <p>
          We may update these terms — the date above changes when we do, and continued use means acceptance. You
          can stop using NewsLayer and delete your account at any time.
        </p>

        <H>8. Contact</H>
        <p>
          Questions about these terms:{" "}
          <a href="mailto:newslayerapp@gmail.com" className="text-[#00E5FF] hover:underline">newslayerapp@gmail.com</a>.
          See also our <Link to="/privacy" className="text-[#00E5FF] hover:underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
